import { utils as coreUtils, PhoneVerification } from '@adc/ui-core'
import * as graph from '@adc/sdk'
import type { Party } from './types'

const { formatTitleCase } = coreUtils.formatters
const { formatPhoneNumber } = PhoneVerification.utils

const MERGE_RULES = [
  {
    primary: graph.enums.ReoOfferPartyRole.EscrowCompany,
    contact: graph.enums.ReoOfferPartyRole.EscrowCompanyContact,
    includesLicense: false,
  },
  {
    primary: graph.enums.ReoOfferPartyRole.BuyerAgentFirm,
    contact: graph.enums.ReoOfferPartyRole.BuyerAgent,
    includesLicense: true,
  },
]

const createContactInfo = (contactParty: Party | undefined) => {
  if (!contactParty) return {}

  return {
    contactEmail: contactParty.email_address || undefined,
    contactPhone: contactParty.phone_number || undefined,
    contactName:
      contactParty?.full_name ||
      (contactParty?.first_name || contactParty?.last_name
        ? `${contactParty.first_name || ''} ${
            contactParty.last_name || ''
          }`.trim()
        : undefined),
  }
}

const createLicenseInfo = (party: Party | undefined) => {
  if (!party) return {}

  return {
    license_number: party.license_number || undefined,
    license_state: party.license_state || undefined,
    license_type: party.license_type || undefined,
  }
}

export const mergeParties = (parties: Party[]): Party[] => {
  const mergedParties: Party[] = []
  const processed = new Set<number>()

  MERGE_RULES.forEach(rule => {
    const primaryIndex = parties.findIndex(p => p.role === rule.primary)
    const contactIndex = parties.findIndex(p => p.role === rule.contact)

    if (primaryIndex !== -1 && contactIndex !== -1) {
      const merged: Party = {
        ...parties[primaryIndex],
        ...createContactInfo(parties[contactIndex]),
        ...(rule.includesLicense
          ? createLicenseInfo(parties[contactIndex])
          : {}),
      }
      mergedParties.push(merged)
      processed.add(primaryIndex)
      processed.add(contactIndex)
    }
  })

  parties.forEach((party, index) => {
    if (!processed.has(index)) {
      mergedParties.push(party)
    }
  })

  return mergedParties
}

export const transformPartyData = (party: Party): Party => {
  const personalName =
    party.first_name || party.last_name
      ? `${party.first_name || ''} ${party.last_name || ''}`.trim()
      : party.full_name || ''

  const companyName = party.company_name || ''
  const displayName = personalName || companyName

  const address = [
    party.street_number,
    party.street_name,
    party.street_unit,
    party.city,
    party.state,
    party.postal_code,
  ]
    .filter(Boolean)
    .join(' ')

  return {
    ...party,
    phone_number: party.phone_number
      ? formatPhoneNumber(party.phone_number)
      : undefined,
    name: displayName,
    personalName,
    companyName,
    address,
    displayLabel: formatTitleCase(party.role),
  }
}

export const processParties = (parties: Party[]): Party[] => {
  const mergedParties = mergeParties(parties)
  return mergedParties.map(party => transformPartyData(party))
}
